import React, { useEffect } from 'react';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { SWRConfig } from 'swr';
import { getSingleActionLayout } from '~/components/pages/other/getSingleActionLayout';
import { MaintenancePage } from '~/components/pages/other/MaintenancePage';
import { envManager } from '~/constants/envManager';
import { ConfirmModalProvider, useModalConfirm } from '~/hooks/common/useConfirmModal';
import { useBeforePopStateHandlers } from '~/hooks/config/useBeforePopStateHandlers';
import { useChatbot } from '~/hooks/config/useChatbot';
import { useConfigReactDatepicker } from '~/hooks/config/useConfigReactDatepicker';
import { useCustomHead } from '~/hooks/config/useCustomHead';
import { useDevelopmentFeatureFlagSetter } from '~/hooks/config/useDevelopmentFeatureFlagSetter';
import { useEnvironmentViewer } from '~/hooks/config/useEnvFlagViewer';
import { useGoogleTagManager } from '~/hooks/config/useGoogleTagManager';
import { useProgressBar } from '~/hooks/config/useProgressBar';
import { useRouteChangeCompleteListener } from '~/hooks/config/useRouteChangeCompleteListener';
import { useStyles } from '~/hooks/config/useStyles';
import { useViewportTrimmer } from '~/hooks/config/useViewportTrimmer';
import { useWarnSelfXss } from '~/hooks/config/useWarnSelfXss';
import { useWatchRevisionUp } from '~/hooks/config/useWatchRevisionUp';
import { CategoryContextProvider } from '~/hooks/context/useCategoryContext';
import { FlashMessagesProvider } from '~/hooks/context/useFlashMessages';
import { ToastMessagesProvider } from '~/hooks/context/useToastMessages';
import { global } from '~/styles/global';
import { reset } from '~/styles/reset';
import { LayoutComponent, NextPageWithLayout } from '~/types/layout';
import 'react-toastify/dist/ReactToastify.css';

if (envManager.publicEnv.environment === 'test') {
  const MockServer = () => import('~/mocks/api/worker');
  MockServer();
}

const LayoutEmpty: LayoutComponent = (props) => <>{props.children}</>;

const Layout: React.FC<{
  children: React.ReactNode;
  layoutComponent: LayoutComponent | undefined;
}> = (props) => {
  const InnerLayout = props.layoutComponent || LayoutEmpty;
  return <InnerLayout>{props.children}</InnerLayout>;
};

const MainApp: React.FC<{
  appProps: AppProps;
}> = ({ appProps }) => {
  const router = useRouter();
  const confirmModal = useModalConfirm();

  useWatchRevisionUp(async () => {
    if (
      await confirmModal.confirm({
        title: '最新版の企業用/店舗用管理画面を利用できます。',
        body: 'ページを再読み込みすると、自動でアップデートされます。',
        applyText: '今すぐ再読み込み',
        cancelText: '今は利用しない',
      })
    )
      location.reload();
  });

  // custom layout
  // https://adamwathan.me/2019/10/17/persistent-layout-patterns-in-nextjs/
  const Component: NextPageWithLayout = appProps.Component;

  // hooks
  useWarnSelfXss();
  useViewportTrimmer(375);
  useConfigReactDatepicker();
  useDevelopmentFeatureFlagSetter();
  useRouteChangeCompleteListener();

  const chatbot = useChatbot({
    show: !!router.asPath.match(/^\/clients\/\d+\/contact$/),
  });
  const customHead = useCustomHead({ title: Component.config?.pageTitle });
  const tagManager = useGoogleTagManager({
    gtmId: envManager.publicEnv.gtmId,
    gtmAuth: envManager.publicEnv.gtmAuth,
    gtmPreview: envManager.publicEnv.gtmPreview,
  });
  const progressBar = useProgressBar();
  const globalStyles = useStyles([global, reset]);
  const environmentViewer = useEnvironmentViewer();
  useBeforePopStateHandlers();

  // NOTE: https://app-new.taimee.co.jpでアクセスされた場合、app-newのログイン画面へリダイレクト
  // NOTE: 本来、monorepo等で解決すべきだが、React/Nextアップグレード前の一次的対処として実装。
  useEffect(() => {
    if (envManager.publicEnv.targetDomain === 'client' && router.asPath.match(/^\/$/))
      router.replace('/login');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.StrictMode>
      <SWRConfig value={{ dedupingInterval: 10_000 }}>
        <CategoryContextProvider>
          <ToastMessagesProvider>
            <FlashMessagesProvider pathname={router.pathname}>
              <Layout layoutComponent={Component.Layout}>
                {/* NOTE: dev環境Safariにてアクセスした場合、TypeErrorが表示されるためこの位置でレンダリング*/}
                {environmentViewer.render()}
                <Component {...appProps.pageProps} />
              </Layout>
            </FlashMessagesProvider>
          </ToastMessagesProvider>
        </CategoryContextProvider>
      </SWRConfig>

      {customHead.render()}
      {progressBar.renderStyles()}
      {globalStyles.renderStyles()}
      {tagManager.render()}
      {chatbot.render()}
    </React.StrictMode>
  );
};

const UnderMaintenanceApp: React.FC = () => {
  const globalStyles = useStyles([global, reset]);
  const Layout = getSingleActionLayout();

  return (
    <>
      <Layout>
        <MaintenancePage />
      </Layout>
      {globalStyles.renderStyles()}
    </>
  );
};

const CustomApp = (appProps: AppProps) => {
  if (envManager.publicEnv.applicationMode === 'under_maintenance') return <UnderMaintenanceApp />;

  return (
    <ConfirmModalProvider>
      <MainApp appProps={appProps} />
    </ConfirmModalProvider>
  );
};

export default CustomApp;
